import Component from '@glimmer/component';
import { service } from '@ember/service';
import { VStack } from 'tio-reimagined/components/layout';
import Heading from 'tio-reimagined/components/heading';
import Text from 'tio-reimagined/components/text';
import Card from 'tio-reimagined/components/card';
import Chip from 'tio-reimagined/components/chip';
import Button from 'tio-reimagined/components/button';
import SectionHeader from 'tio-reimagined/components/section-header';
import DataIndicator from 'tio-employee/components/reimagined/data-indicator';
import type { DataSource } from 'tio-employee/components/reimagined/data-indicator';
import type { DataSourceInfo } from 'tio-employee/services/reimagined-data';
import type ReimaginedDataService from 'tio-employee/services/reimagined-data';
import type SessionContextService from 'tio-employee/services/session-context';
import {
  PhUserCircle,
  PhEnvelope,
  PhBuildings,
  PhBriefcase,
  PhCalendar,
  PhShieldCheck,
  PhGlobe,
  PhKey,
  PhBell,
  PhPalette,
  PhDeviceMobile,
  PhPencil,
} from 'ember-phosphor-icons';
import PageHero from 'tio-reimagined/components/page-hero';
import { getCurrentDemoClientData } from 'tio-employee/utils/career-pathways-demo-data';

interface ProfileModel {
  dataSourceInfo: DataSourceInfo;
  employee: unknown;
  person: unknown;
}

interface ProfileSignature {
  Args: {
    model: ProfileModel;
  };
}

interface ProfileField {
  label: string;
  value: string;
  icon: typeof PhUserCircle;
  editable: boolean;
}

export default class ProfilePage extends Component<ProfileSignature> {
  @service declare reimaginedData: ReimaginedDataService;
  @service declare sessionContext: SessionContextService;

  get isDemoMode() {
    return this.reimaginedData.isDemoMode;
  }

  get profileSource(): DataSource {
    if (this.isDemoMode) return 'demo';
    return this.args.model.employee ? 'live' : 'placeholder';
  }

  get demoClient() {
    return getCurrentDemoClientData();
  }

  get displayName() {
    if (this.isDemoMode) {
      const emp = this.demoClient?.employee;
      return emp ? `${emp.firstName} ${emp.lastName}` : 'Demo User';
    }
    const employee = this.sessionContext.currentEmployee;
    return employee ? `${employee.firstName} ${employee.lastName}` : 'Your Name';
  }

  get email() {
    if (this.isDemoMode) return 'employee@company.com';
    const primaryEmail = this.sessionContext.user?.person?.primaryEmail;
    if (!primaryEmail) return 'Not set';
    return typeof primaryEmail === 'string' ? primaryEmail : String(primaryEmail);
  }

  get employer() {
    if (this.isDemoMode) return this.demoClient.client.name;
    return this.sessionContext.company?.name ?? 'Your Employer';
  }

  get title() {
    if (this.isDemoMode) return 'Software Engineer';
    return this.sessionContext.currentEmployee?.title ?? 'Not set';
  }

  get department() {
    if (this.isDemoMode) return 'Engineering';
    return this.sessionContext.currentEmployee?.department ?? 'Not set';
  }

  get startDate() {
    if (this.isDemoMode) return 'Jan 2021';
    const date = this.sessionContext.currentEmployee?.employmentStartDate;
    if (!date) return 'Not set';
    return new Date(date).toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
  }

  get profileFields(): ProfileField[] {
    return [
      { label: 'Full Name', value: this.displayName, icon: PhUserCircle, editable: false },
      { label: 'Email', value: this.email, icon: PhEnvelope, editable: false },
      { label: 'Employer', value: this.employer, icon: PhBuildings, editable: false },
      { label: 'Job Title', value: this.title, icon: PhBriefcase, editable: false },
      { label: 'Department', value: this.department, icon: PhBriefcase, editable: false },
      { label: 'Start Date', value: this.startDate, icon: PhCalendar, editable: false },
    ];
  }

  <template>
    {{! template-lint-disable no-bare-strings }}
    <VStack class="gap-8 pb-12">
      {{! Profile Header }}
      <PageHero @gradient="brand" @decoration="circles" @rounded="2xl" class="mb-0">
        <:default>
          <div class="tio-row-extra-loose">
            <div
              class="flex h-20 w-20 items-center justify-center rounded-full bg-white/20 text-3xl font-bold"
            >
              {{! First initial }}
              <span class="text-white">
                {{this.displayName}}
              </span>
            </div>
            <div>
              <Heading @level={{2}} class="text-white font-bold">
                {{this.displayName}}
              </Heading>
              <Text class="text-indigo-200">{{this.title}} at {{this.employer}}</Text>
              <div class="flex gap-2 mt-2">
                <Chip @intent="primary">Active Employee</Chip>
              </div>
            </div>
          </div>
        </:default>
      </PageHero>

      {{! Personal Information }}
      <SectionHeader
        @icon={{PhUserCircle}}
        @title="Personal Information"
        @subtitle="Your account details"
        @iconClass="tio-fg-info"
      />
      <DataIndicator @source={{this.profileSource}} @showBadge={{true}}>
        <Card @padding="none" class="divide-y divide-gray-100">
          {{#each this.profileFields as |field|}}
            <div class="flex items-center justify-between p-4">
              <div class="tio-row">
                <div class="flex h-10 w-10 items-center justify-center rounded-lg tio-bg-info-soft">
                  <field.icon class="h-5 w-5 tio-fg-info" />
                </div>
                <div>
                  <Text class="tio-fg-muted text-xs uppercase tracking-wide">{{field.label}}</Text>
                  <div class="font-medium tio-fg-primary">{{field.value}}</div>
                </div>
              </div>
              {{#if field.editable}}
                <button
                  type="button"
                  class="tio-fg-info hover:tio-fg-info-h p-2 rounded-lg hover:tio-bg-info-soft transition-colors"
                >
                  <PhPencil class="h-4 w-4" />
                </button>
              {{/if}}
            </div>
          {{/each}}
        </Card>
      </DataIndicator>

      {{! Security Settings }}
      <SectionHeader
        @icon={{PhShieldCheck}}
        @title="Security"
        @subtitle="Keep your account safe"
        @iconClass="tio-fg-success"
      />
      <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Card @padding="lg">
          <div class="tio-row mb-4">
            <div class="flex h-10 w-10 items-center justify-center rounded-lg tio-bg-success-soft">
              <PhKey class="h-5 w-5 tio-fg-success" />
            </div>
            <div>
              <Heading @level={{4}}>Password</Heading>
              <Text class="tio-fg-muted text-xs">Last changed 45 days ago</Text>
            </div>
          </div>
          <Button @intent="primary" @appearance="outlined" class="w-full">
            Change Password
          </Button>
        </Card>

        <Card @padding="lg">
          <div class="tio-row mb-4">
            <div class="flex h-10 w-10 items-center justify-center rounded-lg tio-bg-info-soft">
              <PhDeviceMobile class="h-5 w-5 tio-fg-info" />
            </div>
            <div>
              <Heading @level={{4}}>Two-Factor Auth</Heading>
              <Chip @intent="success">Enabled</Chip>
            </div>
          </div>
          <Button @intent="primary" @appearance="outlined" class="w-full">
            Manage 2FA
          </Button>
        </Card>
      </div>

      {{! Preferences }}
      <SectionHeader
        @icon={{PhPalette}}
        @title="Preferences"
        @subtitle="Customize your experience"
        @iconClass="tio-fg-brand-accent"
      />
      <div class="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card @padding="lg">
          <div class="tio-row mb-3">
            <div class="flex h-10 w-10 items-center justify-center rounded-lg tio-bg-brand-soft">
              <PhGlobe class="h-5 w-5 tio-fg-brand" />
            </div>
            <Heading @level={{4}}>Language</Heading>
          </div>
          <Text class="tio-fg-secondary text-sm mb-3">
            Set your preferred language for the interface.
          </Text>
          <Chip @intent="default">English (US)</Chip>
        </Card>

        <Card @padding="lg">
          <div class="tio-row mb-3">
            <div class="flex h-10 w-10 items-center justify-center rounded-lg tio-bg-warning-soft">
              <PhBell class="h-5 w-5 tio-fg-warning" />
            </div>
            <Heading @level={{4}}>Notifications</Heading>
          </div>
          <Text class="tio-fg-secondary text-sm mb-3">
            Control email and push notifications.
          </Text>
          <Chip @intent="success">All Enabled</Chip>
        </Card>

        <Card @padding="lg">
          <div class="tio-row mb-3">
            <div class="flex h-10 w-10 items-center justify-center rounded-lg tio-bg-danger-soft">
              <PhPalette class="h-5 w-5 tio-fg-danger" />
            </div>
            <Heading @level={{4}}>Appearance</Heading>
          </div>
          <Text class="tio-fg-secondary text-sm mb-3">
            Toggle dark mode and layout preferences.
          </Text>
          <Chip @intent="default">System Default</Chip>
        </Card>
      </div>
    </VStack>
  </template>
}
